import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAdmin } from '../context/AdminContext';
import {
  LayoutDashboard,
  FileText,
  Settings,
  Users,
  LogOut,
  PlusCircle,
  Mic,
  Video,
  History,
  CheckSquare,
  X
} from 'lucide-react';
import logoPath from '../logo.webp';

interface AdminSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout, stats } = useAdmin();
  const location = useLocation();

  if (!user) return null;

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const NavItem = ({ to, icon: Icon, label, badge }: any) => (
    <Link
      to={to}
      onClick={onClose}
      className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 group ${isActive(to)
          ? 'bg-brand-900 text-white shadow-lg translate-x-1'
          : 'text-gray-500 hover:bg-gray-100 hover:text-brand-900'
        }`}
    >
      <div className="flex items-center gap-3">
        <Icon size={20} className={isActive(to) ? 'text-brand-500' : ''} />
        <span className="font-bold text-sm tracking-tight">{label}</span>
      </div>
      {badge > 0 && (
        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${isActive(to) ? 'bg-brand-50 text-brand-900' : 'bg-brand-500 text-white'
          }`}>{badge}</span>
      )}
    </Link>
  );

  const sidebarContent = (
    <div className="h-full flex flex-col bg-white">
      {/* 1. Locked Top Header */}
      <div className="p-8 pb-4 flex-shrink-0">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-3">
            <img src={logoPath} alt="Nida logo" className="w-20 md:w-28 h-auto block object-contain" />
          </Link>
          <button onClick={onClose} className="md:hidden p-2 text-gray-400 hover:text-brand-900">
            <X size={20} />
          </button>
        </div>
      </div>

      {/* 2. Scrollable Navigation - This part scrolls independently */}
      <div className="flex-1 overflow-y-auto no-scrollbar px-8 py-2">
        <div className="space-y-8 pb-4">
          {/* Main Navigation */}
          <div>
            <h5 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-4 px-4">Workspace</h5>
            <nav className="space-y-1">
              <NavItem to="/admin/dashboard" icon={LayoutDashboard} label="Dashboard" />
              <NavItem to="/admin/content" icon={FileText} label="Content Library" />

              {(user.role === 'journalist' || user.role === 'root') && (
                <NavItem to="/admin/content/new" icon={PlusCircle} label="New Draft" />
              )}

              {(user.role === 'editor' || user.role === 'admin' || user.role === 'root') && (
                <NavItem to="/admin/review" icon={CheckSquare} label="Review Queue" badge={stats.pendingReviews} />
              )}
            </nav>
          </div>

          {/* Media Production (Role Guarded) */}
          {(user.role === 'producer' || user.role === 'admin' || user.role === 'editor' || user.role === 'root') && (
            <div>
              <h5 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-4 px-4">Production</h5>
              <nav className="space-y-1">
                <NavItem to="/admin/audio" icon={Mic} label="Audio Studio" />
                <NavItem to="/admin/video" icon={Video} label="Video Studio" />
              </nav>
            </div>
          )}

          {/* System (Admin Only) */}
          {/* System (Admin Only) */}
          {(user.role === 'admin' || user.role === 'root') && (
            <div>
              <h5 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-4 px-4">System</h5>
              <nav className="space-y-1">
                <NavItem to="/admin/users" icon={Users} label="Staff Directory" />
                <NavItem to="/admin/logs" icon={History} label="Audit Trails" />
                <NavItem to="/admin/settings" icon={Settings} label="Portal Settings" />
              </nav>
            </div>
          )}
        </div>
      </div>

      {/* 3. Locked User Profile Footer - Stays at bottom */}
      <div className="p-8 pt-4 flex-shrink-0 border-t border-gray-100 bg-white shadow-[0_-4px_20px_-10px_rgba(0,0,0,0.05)] z-10">
        <div className="bg-gray-50 rounded-2xl p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full border-2 border-white shadow-sm overflow-hidden flex-shrink-0 bg-gray-200">
              <img src={(user as any).avatar || (user as any).avatar_url || ''} className="w-full h-full object-cover" alt={user.name} />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-sm text-brand-900 truncate leading-tight">{user.name}</p>
              <p className={`text-[10px] font-black uppercase tracking-widest ${user.role === 'root' ? 'text-brand-900 bg-brand-500/20 px-1.5 py-0.5 rounded' : 'text-brand-500'}`}>{user.role}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-white border border-gray-200 rounded-xl text-xs font-bold text-brand-500 hover:text-brand-900 hover:border-brand-900 transition-all shadow-sm active:scale-95"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden md:flex w-64 flex-shrink-0 bg-white border-r border-gray-200 h-[100dvh] sticky top-0 overflow-hidden z-30 shadow-sm">
        {sidebarContent}
      </aside>

      <div className={`md:hidden fixed inset-0 z-[100] transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-brand-900/60 backdrop-blur-sm" onClick={onClose} />
        <aside className={`absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl transition-transform duration-300 overflow-hidden ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          {sidebarContent}
        </aside>
      </div>
    </>
  );
};